import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { HistoryField } from 'components/Field/HistoryField';
import { ReviewStatusField } from 'components/Field/ReviewStatusField';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Pressable } from 'components/Pressable';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { ImageDisplay } from 'componentsAware/ImageDisplay';
import { format } from 'date-fns';
import { useConfirm } from 'hooks/useConfirm';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { formatCurrency, randomKey, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { pick, toInteger } from 'lodash';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import React from 'react';
import { useDispatch } from 'react-redux';
import { MiniPlanAssessment } from 'screens/FieldComponents/MiniPlanAssessment';
import {
  eventAseSelectFindingQuestion,
  NAVIGATE_MODAL,
} from 'store/app/actions';
import {
  doAseDeleteAction,
  doAseLoadPlans,
  doAseOpenAction,
  doAseReviewAction,
  doAseUpdateAction,
} from 'store/assessments/actions';
import {
  selectAseActionAstAse,
  selectAseCategories,
  selectAseIsEditable,
  selectAseTimescales,
  selectAseAccounts,
  selectAseSeverities,
  selectAseTeams,
  selectAseTaskStatuses,
  selectAseIsOnline,
  selectAseContext,
} from 'store/assessments/selectors';
import { returnDefaultAction } from 'store/assessments/templates';
import { AssessmentItemTypes } from 'store/assessments/types';
import { appContextToFileContext } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSESSMENT],
  Screens.ASE_ACTION
>;

export const ActionDisplay: React.FC<{
  navigation: Pick<Props['navigation'], 'goBack'>;
  route: Pick<Props['route'], 'params'>;
}> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const screenSize = useScreenSize();
  const context = useSelector(selectAseContext)!;
  const { id, parent } = route.params;

  const selectedAction = useSelector((state) =>
    selectAseActionAstAse(state, id),
  );

  const action =
    selectedAction ||
    returnDefaultAction({
      sectionId: '1',
      questionId: null,
      fieldInstanceId: null,
      suggestionId: null,
    });

  const editable = useSelector(selectAseIsEditable);
  const isOnline = useSelector(selectAseIsOnline);
  const isQc = editable && isOnline;

  const taskStatuses = useSelector(selectAseTaskStatuses);
  const accounts = useSelector(selectAseAccounts);
  const teams = useSelector(selectAseTeams);
  const categories = useSelector(selectAseCategories);
  const severities = useSelector(selectAseSeverities);
  const timescales = useSelector(selectAseTimescales);

  const descriptionValue = action.description || 'No Description';
  const whyValue = action.why || null;
  const locationValue = action.where || null;
  const costValue =
    action.cost !== null ? formatCurrency(toInteger(action.cost)) : null;
  const pinValue = action.pin;
  const category = categories[action.categoryId] || categories['0']!;
  const severity = severities[action.severityId] || severities['0']!;
  const timescale = timescales[action.timescaleId] || timescales['0']!;
  const imageIds = action.photoIds;

  const dueValue = action.dueDate
    ? format(new Date(action.dueDate), 'dd MMMM yyyy')
    : null;

  const teamsValue = naturalOrderBy(
    action.assignedTeamIds.reduce<string[]>((acc, teamId) => {
      const team = teams[teamId];
      if (team) {
        acc.push(team.name);
      }
      return acc;
    }, []),
  ).join(', ');

  const peopleValue = naturalOrderBy(
    action.assignedAccountIds.reduce<string[]>((acc, accountId) => {
      const account = accounts[accountId];
      if (account) {
        acc.push(account.name);
      }
      return acc;
    }, []),
  ).join(', ');

  const handleReviewStatusChange = (reviewStatusId: string) =>
    dispatch(
      doAseReviewAction({
        id,
        parent,
        reviewStatusId,
      }),
    );

  const handleEditPress = () =>
    dispatch(
      doAseOpenAction({
        id: action.id,
        parent,
        edit: true,
      }),
    );

  const handleDelete = () =>
    confirm({
      text: action.isReview
        ? 'Are you sure you want to delete the review for this action?'
        : 'Are you sure you want to delete this action?',
      title: action.isReview ? 'Delete Review' : 'Delete Action',
      confirmText: 'Delete',
      onConfirm: () => dispatch(doAseDeleteAction({ id: action.id })),
      destructive: true,
    });

  const handleDuplicatePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.AseAction,
        params: {
          id: '',
          isDuplicate: true,
          parent,
          data: {
            ...returnDefaultAction({
              questionId: action.questionId,
              sectionId: action.sectionId,
              fieldInstanceId: action.fieldInstanceId,
              suggestionId: action.suggestionId,
            }),
            ...pick(action, [
              'description',
              'why',
              'where',
              'categoryId',
              'timescaleId',
              'severityId',
              'dueDate',
              'dueDeterminedBy',
              'dueTimescale',
              'dueTypeId',
              'assignedAccountIds',
              'assignedTeamIds',
              'quantity',
              'cost',
            ]),
          },
        },
      }),
    );

  // const handleSuggestTemplate = () =>
  //   !action.questionId
  //     ? dispatch(
  //         ALERT({
  //           text:
  //             'Actions cannot be suggested for sections. ' +
  //             'Move this action to a question or checklist before turning it into a suggestion',
  //         }),
  //       )
  //     : dispatch(
  //         NAVIGATE_MODAL({
  //           screen: ModalScreens.AseMakeSuggestion,
  //           params: {
  //             type: 'action',
  //             description: action.description,
  //             why: action.why,
  //             categoryId: category.id,
  //             severityId: severity.id,
  //             timescaleId: timescale.id,
  //             dueDeterminedBy: action.dueDeterminedBy,
  //             dueTimescale: action.dueTimescale,
  //             dueTypeId: action.dueTypeId || '',
  //             questionId: action.questionId,
  //             answerId: answerId || undefined,
  //           },
  //         }),
  //       );

  const selectQuestionEventKey = React.useRef(randomKey()).current;
  const handleMovePress = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.ASEFINDINGBELONGSTO,
        params: {
          findingType: AssessmentItemTypes.ACTION,
          questionId: action.questionId,
          sectionId: action.sectionId,
          fieldInstanceId: action.fieldInstanceId,
          eventKey: selectQuestionEventKey,
        },
      }),
    );

  React.useEffect(() => {
    const eventSelectFindingQuestion = doAddEvent(
      eventAseSelectFindingQuestion,
      (event) => {
        if (event.payload.key === selectQuestionEventKey) {
          dispatch(
            doAseUpdateAction({
              id,
              data: { ...action, ...event.payload.data },
            }),
          );
        }
      },
    );
    return () => {
      doRemoveEvent(eventSelectFindingQuestion);
    };
  }, [action, dispatch, id, selectQuestionEventKey]);

  return (
    <PageScrollView
      isInModal={!!parent}
      header={
        <NavigationHeader
          heading="Action"
          onPressBackButton={
            screenSize === 'small' ? navigation.goBack : undefined
          }
          onPressSaveButton={
            isQc && (!action.isReview || !!action.reviewStatusId)
              ? handleEditPress
              : undefined
          }
          saveButtonText="Edit"
          menuRight={
            isQc && (!action.isReview || !!action.reviewStatusId)
              ? {
                  iconButtonConfig: {
                    icon: ICONS.EllipsisLight,
                  },
                  options: [
                    {
                      text: 'Move',
                      onPress: handleMovePress,
                      icon: ICONS.HandLight,
                    },
                    {
                      text: 'Duplicate',
                      onPress: handleDuplicatePress,
                      icon: ICONS.CopyLight,
                    },
                    // {
                    //   text: 'Suggest',
                    //   icon: ICONS.LightbulbOnLight,
                    //   onPress: handleSuggestTemplate,
                    // },
                    '-',
                    {
                      text: action.isReview ? 'Delete Review' : 'Delete Action',
                      onPress: handleDelete,
                      icon: ICONS.TrashCanLight,
                      destructive: true,
                    },
                  ],
                }
              : undefined
          }
        />
      }
    >
      <Card>
        <ViewWithSeparators horizontal style={{ flex: 1 }}>
          <CardRow
            icon={ICONS.RBPlectrumSolid}
            label={
              action.reference
                ? rbAddReferencePrefix(action.reference)
                : undefined
            }
            label2={action.customReference}
            style={{ flex: 1 }}
          >
            {!action.reference && (
              <Txt>
                {rbAddReferencePrefix('')}
                <Txt style={{ color: theme.colors.placeholder }}>000000</Txt>
              </Txt>
            )}
          </CardRow>
          <HistoryField
            history={action.history}
            onPress={() =>
              dispatch(
                NAVIGATE_MODAL({
                  screen: ModalScreens.ASEHISTORY,
                  params: {
                    id,
                    findingType: AssessmentItemTypes.ACTION,
                  },
                }),
              )
            }
          />
        </ViewWithSeparators>
      </Card>

      {editable && action.isReview && !action.reviewStatusId ? (
        <Card label="Choose a Status to Start Review">
          <ReviewStatusField
            options={taskStatuses.map((status) => ({
              label: status.name,
              value: status.id,
            }))}
            value={action.reviewStatusId}
            onChange={handleReviewStatusChange}
          />
        </Card>
      ) : (
        !!action.reviewStatusId && (
          <Card>
            <CardRow
              labelAlignTop
              label="Reviewed As"
              value={
                taskStatuses.find(
                  ({ id: reviewStatusId }) =>
                    reviewStatusId === action.reviewStatusId,
                )?.name ?? 'Unknown Status'
              }
            />
            {!!action.reviewNote && (
              <CardRow
                labelAlignTop
                label="Review Note"
                value={action.reviewNote}
              />
            )}
          </Card>
        )
      )}

      <Card>
        <CardRow labelAlignTop label="Description" value={descriptionValue} />
        {!!whyValue && <CardRow labelAlignTop label="Why" value={whyValue} />}
        {!!locationValue && (
          <CardRow labelAlignTop label="Location" value={locationValue} />
        )}
      </Card>

      {(category.id !== '0' ||
        severity.id !== '0' ||
        timescale.id !== '0' ||
        !!dueValue ||
        !!teamsValue ||
        !!peopleValue ||
        !!imageIds.length ||
        costValue !== null ||
        !!pinValue) && (
        <Card>
          {category.id !== '0' && (
            <CardRow
              icon={ICONS.TagLight}
              label="Category"
              value={category.name}
            />
          )}
          {severity.id !== '0' && (
            <CardRow
              icon={ICONS.DiamondExclamationSolid}
              iconColor={`#${severity.colour}`}
              label="Severity"
              value={severity.name}
            />
          )}
          {timescale.id !== '0' && (
            <CardRow
              icon={ICONS.HourglassLight}
              label="Timescale"
              value={timescale.name}
            />
          )}
          {!!dueValue && (
            <CardRow
              icon={ICONS.CalendarDayLight}
              label="Due"
              value={dueValue}
            />
          )}
          {costValue !== null && (
            <CardRow
              icon={ICONS.SterlingSignLight}
              label="Estimated Cost"
              value={costValue}
            />
          )}
          {!!pinValue && (
            <CardRow icon={ICONS.LocationPinLight} label="Pin Location">
              <Pressable
                onPress={() =>
                  dispatch(
                    doAseLoadPlans({
                      pin: {
                        planId: pinValue.planId,
                        x: parseFloat(pinValue.x),
                        y: parseFloat(pinValue.y),
                      },
                      pins: [],
                      selectionKey: undefined,
                    }),
                  )
                }
              >
                <MiniPlanAssessment
                  planId={pinValue.planId}
                  x={parseFloat(pinValue.x)}
                  y={parseFloat(pinValue.y)}
                  maxHeight={100}
                  maxWidth={100}
                />
              </Pressable>
            </CardRow>
          )}
          {!!teamsValue && (
            <CardRow icon={ICONS.UsersLight} label="Teams" value={teamsValue} />
          )}
          {!!peopleValue && (
            <CardRow
              icon={ICONS.UserLight}
              label="People"
              value={peopleValue}
            />
          )}
        </Card>
      )}
      {!!imageIds.length && (
        <Card>
          {!!imageIds.length && (
            <ImageDisplay
              value={imageIds}
              context={appContextToFileContext(context)}
            />
          )}
        </Card>
      )}
    </PageScrollView>
  );
};
